import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * 手写的几个小组件。
 *
 * ⚠ 没有引入 shadcn/ui：M1 只有两个小表单，为它初始化一套组件源码不划算。
 *   等 M2 的对话页真需要弹层/滚动区/骨架屏时再 `pnpm dlx shadcn add`。
 */

type ButtonProps = {
  children: ReactNode
  onClick?: (() => void) | undefined
  type?: 'button' | 'submit'
  variant?: 'primary' | 'ghost' | 'danger'
  disabled?: boolean | undefined
  block?: boolean | undefined
}

const VARIANT: Record<'primary' | 'ghost' | 'danger', string> = {
  primary: 'bg-neutral-900 text-white hover:bg-neutral-700 disabled:bg-neutral-300',
  ghost: 'bg-transparent text-neutral-600 hover:bg-neutral-100 disabled:text-neutral-300',
  danger: 'bg-transparent text-red-600 hover:bg-red-50 disabled:text-red-200',
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled,
  block,
}: ButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-xl px-4 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed ${VARIANT[variant]} ${block ? 'w-full' : ''}`}
    >
      {children}
    </button>
  )
}

type ConfirmButtonProps = {
  label: ReactNode
  /** 展开后那颗确认键的文案；默认用 `common.confirm`。 */
  confirmLabel?: ReactNode
  onConfirm: () => void
  disabled?: boolean | undefined
  variant?: 'primary' | 'ghost' | 'danger'
  block?: boolean
}

/**
 * 两步确认按钮。第一次点击**就地**展开「确认 / 取消」，不弹原生 `confirm()`。
 *
 * ⚠ 为什么不用 `window.confirm`：原生对话框在自动化 / 某些 WebView 里会被直接吞掉
 *   （静默返回 false），于是「删除 / 取消」这类关键动作点了没反应，且 e2e 里根本点不动。
 *   就地展开既能测、又与设计系统一致。
 */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  disabled,
  variant = 'danger',
  block,
}: ConfirmButtonProps) {
  const { t } = useTranslation()
  const [armed, setArmed] = useState(false)

  if (!armed) {
    return (
      <Button variant={variant} disabled={disabled} block={block} onClick={() => setArmed(true)}>
        {label}
      </Button>
    )
  }
  return (
    <span className="flex items-center gap-1">
      <Button
        variant={variant}
        disabled={disabled}
        block={block}
        onClick={() => {
          setArmed(false)
          onConfirm()
        }}
      >
        {confirmLabel ?? t('common.confirm')}
      </Button>
      <Button variant="ghost" onClick={() => setArmed(false)}>
        {t('common.cancel')}
      </Button>
    </span>
  )
}

type TextFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** 字段级错误。⚠ 只有 `fieldErrors` 或明确指向某字段的错误码才该填这里。 */
  error?: string | undefined
  type?: 'text' | 'tel' | 'password'
  inputMode?: 'numeric' | 'tel' | 'text'
  maxLength?: number
  autoComplete?: string
  trailing?: ReactNode
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  error,
  type = 'text',
  inputMode,
  maxLength,
  autoComplete,
  trailing,
}: TextFieldProps) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs text-neutral-500">{label}</span>
      <span
        className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 ${
          error
            ? 'border-red-400 bg-red-50/40'
            : 'border-neutral-200 focus-within:border-neutral-400'
        }`}
      >
        <input
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-300"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          type={type}
          inputMode={inputMode}
          maxLength={maxLength}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          aria-label={label}
        />
        {trailing}
      </span>
      {error ? <span className="text-xs text-red-600">{error}</span> : null}
    </label>
  )
}

type NoticeProps = {
  children: ReactNode
  tone?: 'info' | 'error' | 'success'
  /** ⚠ 兜底错误必须把 traceId 显示出来，否则用户报错时我们查不到链路。 */
  traceId?: string | undefined
}

const TONE: Record<'info' | 'error' | 'success', string> = {
  info: 'bg-neutral-50 text-neutral-600',
  error: 'bg-red-50 text-red-700',
  success: 'bg-emerald-50 text-emerald-700',
}

export function Notice({ children, tone = 'info', traceId }: NoticeProps) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-xl px-3 py-2 text-xs ${TONE[tone]}`}
    >
      {children}
      {traceId ? <span className="ml-1 opacity-60">({traceId})</span> : null}
    </div>
  )
}

type SelectOption = { value: string; label: string }
type SelectProps = {
  label?: string
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  disabled?: boolean | undefined
}

/** 极简下拉。⚠ 用原生 `<select>`：选项少、无需搜索，一个原生控件比自造弹层稳。 */
export function Select({ label, value, onChange, options, disabled }: SelectProps) {
  const control = (
    <select
      className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-neutral-400 disabled:cursor-not-allowed disabled:text-neutral-300"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      aria-label={label}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
  if (!label) return control
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs text-neutral-500">{label}</span>
      {control}
    </label>
  )
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-neutral-100 py-3 text-sm last:border-b-0">
      <span className="shrink-0 text-neutral-500">{label}</span>
      <span className="min-w-0 truncate text-right text-neutral-900">{children}</span>
    </div>
  )
}
